import { doc, updateDoc, getDoc, collection, query, where, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Update merchant order status and trigger email alert
 */
export async function updateMerchantOrderStatus(merchantOrderId, newStatus, sellerId) {
  try {
    console.log(`🔄 [updateMerchantOrderStatus] START - merchantOrderId: ${merchantOrderId}, newStatus: ${newStatus}, sellerId: ${sellerId}`);

    // Validate status
    const validStatuses = ['ACCEPTED', 'READY', 'COMPLETED', 'CANCELLED', 'IN-PROGRESS'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`Invalid status: ${newStatus}. Must be one of: ${validStatuses.join(', ')}`);
    }

    // Get current merchant order data
    console.log(`📖 [updateMerchantOrderStatus] Attempting to read merchant order document...`);
    const merchantOrderRef = doc(db, 'merchant_orders', merchantOrderId);
    let merchantOrderDoc;
    try {
      merchantOrderDoc = await getDoc(merchantOrderRef);
    } catch (readError) {
      console.error(`❌ [updateMerchantOrderStatus] Error reading document:`, readError);
      throw readError;
    }
    
    if (!merchantOrderDoc.exists()) {
      throw new Error(`Merchant order not found: ${merchantOrderId}`);
    }

    console.log(`✅ [updateMerchantOrderStatus] Document read successfully`);
    const currentData = merchantOrderDoc.data();
    
    // Debug: Log sellerId comparison
    console.log('🔍 Seller ID verification:', {
      documentSellerId: currentData.sellerId,
      currentUserSellerId: sellerId,
      match: currentData.sellerId === sellerId,
      documentSellerIdType: typeof currentData.sellerId,
      currentUserSellerIdType: typeof sellerId,
      documentKeys: Object.keys(currentData)
    });
    
    // Verify seller has permission to update this order
    if (currentData.sellerId !== sellerId) {
      console.error('❌ Seller ID mismatch:', {
        documentSellerId: currentData.sellerId,
        currentUserSellerId: sellerId
      });
      throw new Error('Unauthorized: You can only update your own orders');
    }
    
    console.log('✅ Seller ID verified, proceeding with update');

    // Check if status is actually changing (case-insensitive)
    const currentStatusUpper = (currentData.status || '').toUpperCase();
    const newStatusUpper = (newStatus || '').toUpperCase();
    const statusChanged = currentStatusUpper !== newStatusUpper;
    
    if (!statusChanged && currentData.status === newStatus) {
      console.log(`⏭️ Status already ${newStatus}, checking if items need update...`);
      // Even if merchant order status matches, we might need to update items
      // But we should still update the document to ensure consistency and trigger email if needed
    }

    // Convert status to lowercase for items (items use lowercase)
    const itemStatus = newStatus.toLowerCase();

    // Get all order items for this merchant order
    console.log(`📦 [updateMerchantOrderStatus] Fetching order items for merchant order...`);
    const orderItemsCollection = collection(db, 'order_items');
    const orderItemsQuery = query(
      orderItemsCollection,
      where('merchantOrderId', '==', merchantOrderId)
    );

    const itemsSnapshot = await getDocs(orderItemsQuery);
    console.log(`📦 [updateMerchantOrderStatus] Found ${itemsSnapshot.size} order items`);

    // Update all order items and merchant order in a batch
    console.log(`📝 [updateMerchantOrderStatus] Attempting to update ${itemsSnapshot.size} items and merchant order with status: ${newStatus}`);
    const batch = writeBatch(db);
    
    // Update all order items
    const itemIds = [];
    itemsSnapshot.docs.forEach(itemDoc => {
      const itemData = itemDoc.data();
      // Verify item belongs to this seller
      if (itemData.sellerId !== sellerId) {
        console.warn(`⚠️ Skipping item ${itemDoc.id} - sellerId mismatch:`, {
          itemSellerId: itemData.sellerId,
          expectedSellerId: sellerId
        });
        return;
      }
      batch.update(itemDoc.ref, {
        status: itemStatus,
        updatedAt: serverTimestamp()
      });
      itemIds.push(itemDoc.id);
    });

    if (itemIds.length === 0) {
      console.warn(`⚠️ No items found for this merchant order or all items have mismatched sellerId`);
    } else {
      console.log(`✅ Added ${itemIds.length} items to batch update`);
    }

    // Update the merchant order status
    batch.update(merchantOrderRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });

    try {
      await batch.commit();
      console.log(`✅ [updateMerchantOrderStatus] Batch commit successful - updated ${itemIds.length} items and merchant order`);
    } catch (updateError) {
      console.error(`❌ [updateMerchantOrderStatus] Error updating documents:`, updateError);
      console.error(`❌ [updateMerchantOrderStatus] Update error details:`, {
        code: updateError.code,
        message: updateError.message,
        sellerId,
        documentSellerId: currentData.sellerId,
        match: currentData.sellerId === sellerId,
        itemsCount: itemIds.length
      });
      throw updateError;
    }

    console.log(`✅ Merchant order ${merchantOrderId} and ${itemIds.length} items updated to ${newStatus}`);

    return { 
      success: true, 
      message: `Order status updated to ${newStatus}`,
      oldStatus: currentData.status,
      newStatus: newStatus
    };

  } catch (error) {
    console.error('❌ Error updating merchant order status:', error);
    throw error;
  }
}

/**
 * Get merchant order details
 */
export async function getMerchantOrder(merchantOrderId) {
  try {
    const merchantOrderRef = doc(db, 'merchant_orders', merchantOrderId);
    const merchantOrderDoc = await getDoc(merchantOrderRef);
    
    if (!merchantOrderDoc.exists()) {
      throw new Error(`Merchant order not found: ${merchantOrderId}`);
    }

    return {
      id: merchantOrderDoc.id,
      ...merchantOrderDoc.data()
    };

  } catch (error) {
    console.error('❌ Error fetching merchant order:', error);
    throw error;
  }
}

/**
 * Get all merchant orders for a seller
 */
export async function getSellerMerchantOrders(sellerId, limit = 50) {
  try {
    const { collection, query, where, orderBy, limit: queryLimit, getDocs } = await import('firebase/firestore');
    
    const merchantOrdersRef = collection(db, 'merchant_orders');
    const q = query(
      merchantOrdersRef,
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc'),
      queryLimit(limit)
    );
    
    const snapshot = await getDocs(q);
    const merchantOrders = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return merchantOrders;

  } catch (error) {
    console.error('❌ Error fetching seller merchant orders:', error);
    throw error;
  }
}
